import { API_BASE_URL } from '../api';
import apiService from '../services/apiService';

let cache = {
  rows: null,
  fetchedAt: 0,
};

const TTL_MS = 10 * 60 * 1000;

export function getCachedJobsList() {
  if (!Array.isArray(cache.rows)) {
    return null;
  }
  if (Date.now() - cache.fetchedAt > TTL_MS) {
    return null;
  }
  return cache.rows;
}

export function setCachedJobsList(rows) {
  cache = {
    rows: Array.isArray(rows) ? rows : [],
    fetchedAt: Date.now(),
  };
}

/** Owner filter is client-side only — one API load for all roles. */
export function filterJobsByOwnerList(list, ownerFilter, userId) {
  const rows = Array.isArray(list) ? list : [];
  if (!ownerFilter || ownerFilter === 'all') {
    return rows;
  }
  const ownerId = (job) =>
    Number(job?.office_owner_user_id ?? job?.office_owner?.id ?? 0);

  if (ownerFilter === 'mine' && userId) {
    const uid = Number(userId);
    return rows.filter((job) => ownerId(job) === uid);
  }
  if (typeof ownerFilter === 'string' && ownerFilter.startsWith('office_')) {
    const idNum = Number(ownerFilter.slice('office_'.length));
    if (Number.isFinite(idNum) && idNum > 0) {
      return rows.filter((job) => ownerId(job) === idNum);
    }
  }
  return rows;
}

/**
 * Load all collection pages (Dashboard logic). Throws on hard failure.
 */
export async function fetchAllCollectionsFromApi() {
  const perPage = 200;
  let page = 1;
  let lastPage = 1;
  const out = [];

  do {
    const qs = new URLSearchParams({
      per_page: String(perPage),
      page: String(page),
      _: String(Date.now()),
    });
    const url = `${API_BASE_URL}collections?${qs.toString()}`;
    const token = apiService.getToken();

    let response = await fetch(url, {
      method: 'GET',
      headers: token
        ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        : { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (response.status === 401 && token) {
      const refreshed = await apiService.tryRefreshToken();
      if (refreshed) {
        const token2 = apiService.getToken();
        response = await fetch(url, {
          method: 'GET',
          headers: token2
            ? { Authorization: `Bearer ${token2}`, Accept: 'application/json' }
            : { Accept: 'application/json' },
          cache: 'no-store',
        });
      }
    }

    if (!response.ok) {
      let msg = `Could not load jobs (HTTP ${response.status})`;
      try {
        const err = await response.json();
        if (err?.message) msg = err.message;
        else if (err?.error) msg = err.error;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }

    const res = await response.json();
    if (Array.isArray(res)) {
      return res;
    }

    const chunk = Array.isArray(res?.data) ? res.data : [];
    out.push(...chunk);
    lastPage = Math.max(1, Number(res?.last_page ?? res?.meta?.last_page) || 1);
    page += 1;
  } while (page <= lastPage);

  return out;
}

export async function prefetchJobsList() {
  try {
    const rows = await fetchAllCollectionsFromApi();
    setCachedJobsList(rows);
    return rows;
  } catch {
    return null;
  }
}
